import { checkoutApi } from '../api/checkout';
import { paymentsApi } from '../api/payments';
import { cartApi } from '../api/cart';
import secureLocalStorage from 'react-secure-storage';

class CheckoutService {
  /**
   * Transform cart items to order format
   * Only includes fields required by the API
   * Maps both old and new cart data structures
   */
  transformCartToOrderItems(cartItems) {
    console.log('Transforming cart items for order payload:', cartItems);
    return cartItems.map(item => {
      console.log('Transforming cart item:', item);
      // Handle both old cart format (flat) and new format (nested product object)
      const getProductId = () => {
        // New format: item.product._id or item.product.id
        if (item?.product?.id) return item.product.id;
        if (item?.product?._id) return item.product._id;
        // Old format: item.id
        if (item?.id) return item.id;
        return null;
      };

      const getProductName = () => {
        if (item?.product?.name) return item.product.name;
        if (item?.product?.productName) return item.product.productName;
        if (item?.name) return item.name;
        return '';
      };

      const getProductImage = () => {
        if (item?.product?.imageUrl) return item.product.imageUrl;
        if (item?.product?.image) return item.product.image;
        if (item?.product?.featuredImage) return item.product.featuredImage;
        if (item?.product?.thumbailImagePath) return item.product.thumbailImagePath;
        if (item?.image) return item.image;
        return null;
      };

      const getVariationKeyId = () => {
        // Try all possible property names for variation ID
        if (item?.variationId) return item.variationId;
        if (item?.variationKeyId) return item.variationKeyId;
        if (item?.variationKey) return item.variationKey;
        if (item?.product?.variationId) return item.product.variationId;
        if (item?.product?.variationKeyId) return item.product.variationKeyId;
        return null;
      };

      const getIsPreOrder = () => {
        // Check both item-level and product-level isPreOrder property
        if (item?.isPreOrder !== undefined) return item.isPreOrder;
        if (item?.product?.isPreOrder !== undefined) return item.product.isPreOrder;
        return false;
      };

      const product = {
        productId: getProductId(),
        productName: getProductName(),
        quantity: item?.quantity || 1,
        variationKeyId: getVariationKeyId(),
      };

      // Add productImage if available
      const productImage = getProductImage();
      if (productImage) {
        product.productImage = productImage;
      }

      // Add additionalServices if available
      if (item?.additionalServices && item.additionalServices.length > 0) {
        product.additionalServices = item.additionalServices;
      } else if (!item?.additionalServices) {
        product.additionalServices = [];
      }

      // Always include isPreOrder flag (true or false)
      product.isPreOrder = getIsPreOrder();

      return product;
    });
  }

  /**
   * Calculate order totals
   * Gets actual packaging fee from cart instead of using hardcoded value
   */
  calculateOrderTotals(cartItems, deliveryFee = 0, discount = 0) {
    // Calculate subtotal excluding packaging items
    const subtotal = cartItems.reduce((total, item) => {
      // Skip packaging items from subtotal calculation
      if (item?.isPackage === true) {
        return total;
      }
      const basePrice = item?.product?.price || item?.product?.discountedPrice || item?.product?.normalPrice || item?.price || 0;
      const additionalServicesPrice = item?.additionalServicesAmount || 0;
      const itemTotal = (basePrice + additionalServicesPrice) * (item?.quantity || 1);
      return total + itemTotal;
    }, 0);

    // Get actual packaging fee from packaging items in cart
    const packagingItems = cartItems.filter(item => item?.isPackage === true);
    const packagingFee = packagingItems.reduce((total, item) => {
      const price = item?.product?.price || 0;
      return total + (price * (item?.quantity || 1));
    }, 0);

    const tax = Math.round((subtotal - discount) * 0.16); // 16% VAT
    const total = subtotal + deliveryFee + packagingFee + tax - discount;

    return {
      subtotal,
      deliveryFee,
      packagingFee,
      tax,
      discount,
      total
    };
  }

  /**
   * Validate checkout form based on delivery method
   */
  validateCheckoutForm(formData, deliveryMethod) {
    const errors = {};

    // Common validations
    if (!formData.firstName?.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName?.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.phone?.trim()) {
      errors.phone = 'Phone number is required';
    }

    // Delivery method specific validations
    if (deliveryMethod === 'ship') {
      if (!formData.shippingRegionId && !formData.selectedCounty) {
        errors.shippingRegion = 'Please select a delivery region';
      }
      if (!formData.address?.trim()) {
        errors.address = 'Delivery address is required';
      }
      if (!formData.city?.trim()) {
        errors.city = 'City is required';
      }
    } else if (deliveryMethod === 'pickup') {
      if (!formData.pickupLocationId) {
        errors.pickupLocation = 'Please select a pickup location';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Prepare order payload for API
   */
  prepareOrderPayload(formData, cartItems, deliveryMethod) {
    const customerId = secureLocalStorage.getItem('customerId');
    const transformedProducts = this.transformCartToOrderItems(cartItems);

    const payload = {
      products: transformedProducts,
      customerId: customerId,
      deliveryMethod: deliveryMethod
    };

    if (deliveryMethod === 'ship') {
      payload.shippingRegionId = formData.shippingRegionId || formData.selectedShippingRegion;
      payload.shippingCustomerpreference = {
        address: formData.address || null,
        city: formData.city || null,
        apartment: formData.apartment || null,
        county: formData.county || formData.selectedCounty || null,
        postalCode: formData.postalCode || null,
        area: formData.area || formData.selectedArea || null
      };
    } else if (deliveryMethod === 'pickup') {
      payload.pickUpLocationId = formData.pickupLocationId;
      // Pickup doesn't need shipping customer preference
      payload.shippingCustomerpreference = {
        address: null,
        city: null,
        apartment: null,
        county: null,
        postalCode: null
      };
    }

    return payload;
  }

  /**
   * Create order
   */
  async createOrder(formData, cartItems, deliveryMethod) {
    try {
      // Validate form
      const validation = this.validateCheckoutForm(formData, deliveryMethod);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          message: 'Please fill in all required fields'
        };
      }

      // Prepare payload
      const payload = this.prepareOrderPayload(formData, cartItems, deliveryMethod);

      // Create order via API
      const response = await checkoutApi.createOrder(payload);

      if (response.success) {
        return {
          success: true,
          order: response.data,
          message: response.message || 'Order created successfully'
        };
      }

      return {
        success: false,
        message: response.message || 'Failed to create order'
      };
    } catch (error) {
      console.error('Error creating order:', error);
      return {
        success: false,
        message: error.message || 'Failed to create order'
      };
    }
  }

  /**
   * Process payment based on payment method
   */
  async processPayment(order, paymentMethod, paymentData) {
    try {
      let paymentPayload = {
        orderId: order.id || order._id,
        amount: order.totalAmount || order.total,
        paymentMethod: paymentMethod
      };

      switch (paymentMethod) {
        case 'mpesa':
          paymentPayload.phoneNumber = paymentData.mpesaPhone;
          break;
        case 'card':
          paymentPayload.cardDetails = paymentData.cardDetails;
          break;
        case 'cash':
          // Cash on delivery - no immediate payment needed
          return {
            success: true,
            message: 'Order placed successfully. Pay on delivery.'
          };
        default:
          return {
            success: false,
            message: 'Invalid payment method'
          };
      }

      const response = await checkoutApi.initiatePayment(paymentPayload);
      return response;
    } catch (error) {
      console.error('Error processing payment:', error);
      return {
        success: false,
        message: error.message || 'Payment failed'
      };
    }
  }

  /**
   * Complete checkout process
   */
  async completeCheckout(formData, cartItems, deliveryMethod, paymentMethod, paymentData) {
    try {
      // Create order
      const orderResponse = await this.createOrder(formData, cartItems, deliveryMethod);

      if (!orderResponse.success) {
        return orderResponse;
      }

      // Process payment
      const paymentResponse = await this.processPayment(
        orderResponse.order,
        paymentMethod,
        paymentData
      );

      if (!paymentResponse.success && paymentMethod !== 'cash') {
        return {
          success: false,
          message: paymentResponse.message || 'Payment failed',
          order: orderResponse.order
        };
      }

      // Clear cart on successful order
      cartApi.clearCart();

      return {
        success: true,
        message: 'Order placed successfully',
        order: orderResponse.order,
        payment: paymentResponse
      };
    } catch (error) {
      console.error('Error completing checkout:', error);
      return {
        success: false,
        message: error.message || 'Checkout failed'
      };
    }
  }

  /**
   * Format shipping regions for display
   */
  formatShippingRegions(regions) {
    if (!Array.isArray(regions)) return [];

    // Return regions with consistent structure matching API response
    return regions.map(region => ({
      _id: region._id || region.id,
      regionName: region.regionName || region.name,
      regionDescription: region.regionDescription || region.description || '',
      shippingCost: typeof region.shippingCost !== 'undefined' ? region.shippingCost : (region.deliveryFee || region.fee || 0),
      estimatedDays: region.estimatedDays || '5-7 business days',
      areas: region.areas || []
    }));
  }

  /**
   * Format pickup locations for display
   */
  formatPickupLocations(locations) {
    if (!Array.isArray(locations)) return [];

    // Return locations with consistent structure
    return locations.map(location => ({
      _id: location._id || location.id,
      locationName: location.locationName || location.name,
      address: location.address || '',
      phone: location.phone || '',
      workingHours: location.workingHours || 'Mon-Sat: 8AM-6PM',
      landmark: location.landmark || ''
    }));
  }

  /**
   * Check if customer is logged in
   */
  isCustomerLoggedIn() {
    const customerId = secureLocalStorage.getItem('customerId');
    return !!customerId;
  }

  /**
   * Get customer details
   */
  getCustomerDetails() {
    return {
      id: secureLocalStorage.getItem('customerId'),
      name: secureLocalStorage.getItem('customerName'),
      email: secureLocalStorage.getItem('customerEmail'),
      phone: secureLocalStorage.getItem('customerPhone')
    };
  }

  /**
   * Fetch available payment methods and their status
   */
  async fetchPaymentMethods() {
    try {
      const result = await paymentsApi.getPaymentMethods();
      return result;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      return {
        success: false,
        methods: {
          mpesa: false,
          card: false,
          paybill: false,
          momo: false
        }
      };
    }
  }

  /**
   * Validate payment method phone number
   */
  isValidMpesaPhoneNumber(phoneNumber) {
    const phoneNumberRegex = /^254\d{9}$/;
    return phoneNumberRegex.test(phoneNumber);
  }

  /**
   * Setup WebSocket listener for payment status
   */
  setupPaymentListener(orderId, baseUrl, callbacks, statusPayload = null) {
    const socket = new WebSocket(baseUrl + '/notification/' + orderId);
    let settled = false;
    let pollTimer = null;

    const settle = () => {
      settled = true;
      clearTimeout(checkStatusTimer);
      clearTimeout(pollTimer);
      socket.close();
    };

    const checkStatusTimer = setTimeout(() => {
      settle();
      if (callbacks?.onTimeout) {
        callbacks.onTimeout('Payment timeout. Please check your order status.');
      }
    }, 300000); // 5 minutes timeout

    // Fallback poll: some payment callbacks (e.g. KCB paybill STK) complete the
    // order without emitting a WebSocket SUCCESS event, so verify over HTTP too.
    const pollPaymentStatus = async () => {
      if (settled || !statusPayload) return;
      try {
        const result = await paymentsApi.checkPaymentStatus(statusPayload.pushyTransactionId, statusPayload.businessNo);
        if (settled) return;
        if (result?.success) {
          settle();
          if (callbacks?.onSuccess) {
            callbacks.onSuccess('Your order has been processed successfully.');
          }
          return;
        }
      } catch (error) {
        console.error('Payment status poll failed:', error);
      }
      if (!settled) {
        pollTimer = setTimeout(pollPaymentStatus, 15000);
      }
    };
    if (statusPayload) {
      pollTimer = setTimeout(pollPaymentStatus, 45000);
    }

    socket.addEventListener('message', function (event) {
      try {
        const data = JSON.parse(event.data);
        if (data?.status === "OK") {
          if (callbacks?.onInfo) {
            callbacks.onInfo(data?.message || 'Payment processing...');
          }
        } else if (data?.status === "SUCCESS") {
          settle();
          if (callbacks?.onSuccess) {
            callbacks.onSuccess(data?.message || 'Your order has been processed successfully.');
          }
        } else {
          settle();
          if (callbacks?.onError) {
            callbacks.onError(data?.response?.message || data?.statusMessage || 'Failed to process order payments.');
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        settle();
        if (callbacks?.onError) {
          callbacks.onError('Error processing payment response');
        }
      }
    });

    socket.addEventListener('error', function (error) {
      console.error('WebSocket error:', error);
      // Don't fail the payment on a socket error — the fallback poll can still
      // confirm it. Only surface the error when there is no poll running.
      if (!statusPayload) {
        settle();
        if (callbacks?.onError) {
          callbacks.onError('WebSocket connection error');
        }
      }
    });

    return socket;
  }

  /**
   * Initiate STK push for payment
   */
  async initiateStkPush(orderDetails, paymentMethod, phoneNumber, baseUrl, callbacks) {
    try {
      const payload = {
        amount: orderDetails.transamount,
        phone: phoneNumber,
        businessId: orderDetails.businessId,
        orderIds: [orderDetails._id],
        type: 'order',
        paymentChanel: 'Web',
        discount: 0,
        paymentMethod: paymentMethod,
        businessNo: orderDetails.businessId
      };

      const response = await paymentsApi.initiateStkPush(payload);

      if (response.success) {
        const paymentLink = response.paymentLink;
        if (paymentLink) {
          const cacheBustedLink = `${paymentLink}?t=${new Date().getTime()}`;
          setTimeout(() => {
            window.open(cacheBustedLink);
          }, 3000);
        }

        const orderId = response.stkOrderId || response.invoiceNumber || orderDetails._id;
        this.setupPaymentListener(orderId, baseUrl, callbacks, {
          pushyTransactionId: orderId,
          businessNo: orderDetails.businessId
        });

        return {
          success: true,
          message: 'Payment initiated successfully',
          stkOrderId: response.stkOrderId,
          invoiceNumber: response.invoiceNumber
        };
      } else {
        if (callbacks?.onError) {
          callbacks.onError(response.message || 'Failed to initiate payment');
        }
        return {
          success: false,
          message: response.message || 'Failed to initiate payment'
        };
      }
    } catch (error) {
      console.error('Error initiating STK push:', error);
      if (callbacks?.onError) {
        callbacks.onError(error.message || 'Failed to initiate payment');
      }
      return {
        success: false,
        message: error.message || 'Failed to initiate payment'
      };
    }
  }

  /**
   * Check payment status - returns full order details, payment data, and transaction info
   */
  async checkPaymentStatus(pushyTransactionId, businessNo) {
    try {
      const response = await paymentsApi.checkPaymentStatus(pushyTransactionId, businessNo);

      if (response.success) {
        console.log('Payment status retrieved successfully:', {
          orderNumber: response.order?.orderNumber,
          deliveryMethod: response.order?.deliveryMethod,
          paymentStatus: response.data?.paid ? 'PAID' : 'PENDING',
          stkPhone: response.order?.stkPhone
        });
      }

      return response;
    } catch (error) {
      console.error('Error checking payment status:', error);
      return {
        success: false,
        message: error.message || 'Failed to check payment status'
      };
    }
  }
}

const checkoutService = new CheckoutService();
export default checkoutService;